import type { QueueItem, QueueViewSpec, ResolvedFigure } from '../model.js';
import { box, finishFigure, point, roundGeometry, SceneBuilder, validateViewport } from '../scene-builder.js';

const FAMILY = 'queue-view';

/** Spline Sans Mono advance, in ems. */
const MONO_ADVANCE = 0.6;
/** Gabarito average advance, in ems. */
const SANS_ADVANCE = 0.53;
/** The hand adds this tracking to every uppercase run. */
const UPPERCASE_TRACKING = 1.1;

const MICRO = 11;
const VALUE = 12;
const PROSE = 12;

const TICK_STEPS = [10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 2500, 5000, 10000, 20000, 50000, 100000];

const MAX_LABEL_CHARS = 28;
const MAX_LABEL_LINES = 2;

export const QUEUE_ID = 'queue';
export const QUEUE_DEADLINE_ID = 'queue.deadline';
export const QUEUE_CAPACITY_ID = 'queue.capacity';
export const QUEUE_REJECTED_ID = 'queue.rejected';
export const QUEUE_DOOMED_GROUP_ID = 'queue.group.doomed';
export const QUEUE_FLOW_ID = 'queue.flow';
export const QUEUE_CALLOUT_ID = 'queue.callout';
export const QUEUE_DEPTH_TIME_ID = 'queue.depth-time';

export function queueItemId(itemId: string): string {
  return `queue.item.${itemId}`;
}

export function queueOverdueId(itemId: string): string {
  return `${queueItemId(itemId)}.overdue`;
}

export function queueSlotId(slot: number): string {
  return `queue.slot.${slot}`;
}

interface QueueLayout {
  /** Table tier: identity in a left gutter. Stacked tier: identity above its own bar. */
  table: boolean;
  margin: number;
  contentWidth: number;
  railX: number;
  rowsX: number;
  rowsWidth: number;
  indexWidth: number;
  labelX: number;
  labelLines: string[][];
  labelLineCount: number;
  barX: number;
  barWidth: number;
  barHeight: number;
  chipX: number;
  chipWidth: number;
  chipHeight: number;
  rowPitch: number;
  rowGap: number;
  barOffset: number;
  summaryLines: string[];
  calloutTop: number;
  calloutWidth: number;
  calloutHeight: number;
  calloutLines: string[];
  calloutCompact: boolean;
  plotTop: number;
  rowsTop: number;
  rowsBottom: number;
  wallY: number;
  bandY: number;
  bandHeight: number;
  axisY: number;
  step: number;
  domainMax: number;
  deadlineX: number;
  queueMs: number;
  rowsGap: number;
  depthTimeX: number;
  showDepthTime: boolean;
  doomed: QueueItem[];
  shedPerSecond: number;
  full: boolean;
}

/**
 * Compiles a bounded queue into an age figure rather than a fill meter.
 *
 * A fill meter ("6 of 8 slots used") teaches the wrong lesson: it invites a
 * bigger buffer. This view measures how long each queued item has already
 * waited against the client's timeout, so the head of the queue is visibly
 * past its deadline before it is ever served. Arrival and service rates,
 * the hard capacity wall, and the arrivals shed at that wall are drawn from
 * the same semantic facts — the author supplies no geometry.
 */
export function compileQueueView(spec: QueueViewSpec): ResolvedFigure {
  const layout = validateQueueView(spec);
  const builder = new SceneBuilder();
  const { width } = spec.viewport;
  const items = spec.items;
  const taught = items[0]!;
  const taughtId = queueItemId(taught.id);
  const warnings: string[] = [];

  // Registered first so every object drawn later owns the tap that lands on it.
  builder.hit(QUEUE_ID, box(layout.margin, layout.plotTop, layout.contentWidth, layout.axisY - layout.plotTop));

  builder.text(`${QUEUE_ID}.eyebrow`, QUEUE_ID, 'annotation', point(layout.margin, 24), 'BOUNDED QUEUE · AGE, NOT FILL', {
    size: MICRO,
    mono: true,
    weight: 700,
    uppercase: true,
  });
  layout.summaryLines.forEach((line, index) => {
    builder.text(`${QUEUE_ID}.summary.${index}`, QUEUE_ID, 'structure', point(layout.margin, 45 + index * 15), line, {
      size: MICRO,
      mono: true,
      weight: 700,
    });
  });

  // The taught note sits above the queue, marked by the taught element's own rule.
  const calloutBounds = box(layout.margin, layout.calloutTop, layout.calloutWidth, layout.calloutHeight);
  builder.rect(`${QUEUE_ID}.callout.rule`, taughtId, 'positive', box(layout.margin, layout.calloutTop, 3, layout.calloutHeight), {
    radius: 0,
    label: layout.calloutLines.join(' '),
  });
  layout.calloutLines.forEach((line, index) => {
    builder.text(`${QUEUE_ID}.callout.line.${index}`, QUEUE_CALLOUT_ID, 'structure', point(layout.margin + 13, layout.calloutTop + 13 + index * 16), line, {
      size: PROSE,
      weight: 500,
    });
  });
  const leaderX = roundGeometry(layout.labelX - 10);
  builder.line(`${QUEUE_ID}.callout.leader`, taughtId, 'operator', point(leaderX, layout.calloutTop + layout.calloutHeight), point(leaderX, layout.rowsTop - 4), { markerEnd: true });
  builder.anchor(`${QUEUE_ID}.callout.anchor`, taughtId, point(leaderX, layout.calloutTop + layout.calloutHeight), 'down');
  builder.hit(QUEUE_CALLOUT_ID, calloutBounds);
  builder.relation(`${QUEUE_CALLOUT_ID}.annotates`, 'annotates', QUEUE_CALLOUT_ID, taughtId, layout.calloutLines.join(' '));

  // Flow rail: the queue advances upward, one served item at a time.
  builder.line(`${QUEUE_FLOW_ID}.rail`, QUEUE_FLOW_ID, 'operator', point(layout.railX, layout.wallY), point(layout.railX, layout.rowsTop - 4), { markerEnd: true });
  const railLabel = fitText([`SERVICE ${spec.servicePerSecond}/s`, `OUT ${spec.servicePerSecond}/s`, `${spec.servicePerSecond}/s`], layout.wallY - layout.rowsTop - 8);
  builder.text(`${QUEUE_FLOW_ID}.label`, QUEUE_FLOW_ID, 'annotation', point(layout.margin + 5, (layout.rowsTop + layout.wallY) / 2), railLabel, {
    size: MICRO,
    mono: true,
    weight: 700,
    align: 'middle',
    uppercase: true,
    rotate: 90,
  });
  builder.anchor(`${QUEUE_FLOW_ID}.exit`, QUEUE_FLOW_ID, point(layout.railX, layout.rowsTop - 4), 'up');
  builder.anchor(`${QUEUE_FLOW_ID}.entry`, QUEUE_FLOW_ID, point(layout.railX, layout.wallY), 'down');
  builder.hit(QUEUE_FLOW_ID, box(layout.margin, layout.rowsTop - 4, layout.railX - layout.margin + 8, layout.wallY - layout.rowsTop + 4));

  for (let slot = 0; slot < spec.capacity; slot += 1) {
    const rowTop = roundGeometry(layout.rowsTop + slot * layout.rowPitch);
    const barTop = roundGeometry(rowTop + layout.barOffset);
    const barMid = roundGeometry(barTop + layout.barHeight / 2);
    const rowBottom = roundGeometry(barTop + layout.barHeight);
    const item = items[slot];

    if (!item) {
      const emptyId = queueSlotId(slot);
      builder.rect(`${emptyId}.surface`, emptyId, 'construction', box(layout.barX, barTop, layout.barWidth, layout.barHeight), {
        radius: 3,
        label: `slot ${slot + 1} is empty`,
      });
      builder.text(`${emptyId}.index`, emptyId, 'annotation', point(layout.rowsX, layout.table ? barMid + 4 : rowTop + 12), String(slot + 1), {
        size: MICRO,
        mono: true,
        weight: 500,
      });
      builder.text(`${emptyId}.label`, emptyId, 'annotation', point(layout.table ? layout.barX + 12 : layout.labelX, layout.table ? barMid + 4 : rowTop + 12), 'EMPTY SLOT', {
        size: MICRO,
        mono: true,
        weight: 700,
        uppercase: true,
      });
      builder.anchor(`${emptyId}.center`, emptyId, point(layout.barX + layout.barWidth / 2, barMid));
      builder.hit(emptyId, box(layout.rowsX, rowTop, layout.rowsWidth, rowBottom - rowTop + 4));
      const emptyBandTop = slot === 0 ? layout.rowsTop - 8 : roundGeometry(barTop - 4);
      const emptyBandBottom = slot === spec.capacity - 1
        ? roundGeometry(layout.showDepthTime ? layout.rowsBottom + 4 : layout.wallY)
        : roundGeometry(barTop + layout.barHeight + (layout.table ? layout.rowGap : 4));
      builder.line(`${QUEUE_DEADLINE_ID}.band.${slot}`, QUEUE_DEADLINE_ID, 'negative', point(layout.deadlineX, emptyBandTop), point(layout.deadlineX, emptyBandBottom));
      continue;
    }

    const semanticId = queueItemId(item.id);
    const overdueId = queueOverdueId(item.id);
    const doomed = item.waitedMs > spec.clientTimeoutMs;
    const overdueMs = item.waitedMs - spec.clientTimeoutMs;
    const tipX = roundGeometry(layout.barX + (item.waitedMs / layout.domainMax) * layout.barWidth);
    const splitX = roundGeometry(layout.barX + (Math.min(item.waitedMs, spec.clientTimeoutMs) / layout.domainMax) * layout.barWidth);
    const identity = slot === 0 ? 'HEAD' : slot === items.length - 1 ? 'TAIL' : String(slot + 1);
    const identityLabel = slot === 0 || slot === items.length - 1;

    // One bar is the whole wait; the amber tail inside it is the part already lost.
    builder.rect(`${semanticId}.bar`, semanticId, 'header', box(layout.barX, barTop, Math.max(3, tipX - layout.barX), layout.barHeight), {
      radius: 3,
      label: `${item.label} has waited ${item.waitedMs} ms`,
      interactive: true,
    });
    builder.relation(`${semanticId}.within-deadline`, 'wait-inside-deadline', semanticId, QUEUE_DEADLINE_ID, `${item.label} spent ${Math.min(item.waitedMs, spec.clientTimeoutMs)} ms of the ${spec.clientTimeoutMs} ms budget`);

    if (doomed) {
      builder.rect(`${overdueId}.bar`, overdueId, 'negative', box(splitX, barTop, Math.max(3, tipX - splitX), layout.barHeight), {
        radius: 3,
        label: `${item.label} is ${overdueMs} ms past the ${spec.clientTimeoutMs} ms client timeout`,
        interactive: true,
      });
      builder.anchor(`${overdueId}.start`, overdueId, point(splitX, barMid), 'left');
      builder.anchor(`${overdueId}.tip`, overdueId, point(tipX, barMid), 'right');
      builder.hit(overdueId, box(splitX, barTop, Math.max(3, tipX - splitX), layout.barHeight));
      builder.relation(`${overdueId}.past-deadline`, 'past-deadline', semanticId, QUEUE_DEADLINE_ID, `${item.label} passed the ${spec.clientTimeoutMs} ms timeout ${overdueMs} ms ago`);
    }

    // The client deadline crosses this row exactly where the wait stopped being useful.
    const bandTop = slot === 0 ? layout.rowsTop - 8 : roundGeometry(barTop - 4);
    const bandBottom = slot === spec.capacity - 1
      ? roundGeometry(layout.showDepthTime ? layout.rowsBottom + 4 : layout.wallY)
      : roundGeometry(barTop + layout.barHeight + (layout.table ? layout.rowGap : 4));
    builder.line(`${QUEUE_DEADLINE_ID}.band.${slot}`, QUEUE_DEADLINE_ID, 'negative', point(layout.deadlineX, bandTop), point(layout.deadlineX, bandBottom));

    builder.text(`${semanticId}.index`, semanticId, identityLabel ? 'structure' : 'annotation', point(layout.rowsX, layout.table ? barMid + 4 : rowTop + 12), identity, {
      size: MICRO,
      mono: true,
      weight: identityLabel ? 700 : 500,
      uppercase: identityLabel,
    });

    const lines = layout.labelLines[slot] ?? [item.label];
    lines.forEach((line, lineIndex) => {
      builder.text(`${semanticId}.label.${lineIndex}`, semanticId, 'structure', point(layout.table ? layout.barX - 14 : layout.labelX, layout.table ? barMid + 4 : rowTop + 12 + lineIndex * 15), line, {
        size: VALUE,
        mono: true,
        weight: 600,
        align: layout.table ? 'end' : 'start',
      });
    });

    const chipY = roundGeometry(layout.table ? barTop + (layout.barHeight - layout.chipHeight) / 2 : rowTop + 12 - layout.chipHeight + 5);
    builder.rect(`${semanticId}.chip`, semanticId, doomed ? 'negative' : 'surface', box(layout.chipX, chipY, layout.chipWidth, layout.chipHeight), {
      radius: 9,
      label: `${item.label} has waited ${item.waitedMs} ms`,
    });
    builder.text(`${semanticId}.value`, semanticId, 'structure', point(layout.chipX + layout.chipWidth / 2, chipY + layout.chipHeight / 2 + 4), `${item.waitedMs} ms`, {
      size: VALUE,
      mono: true,
      weight: 600,
      align: 'middle',
    });

    builder.anchor(`${semanticId}.origin`, semanticId, point(layout.barX, barMid), 'left');
    builder.anchor(`${semanticId}.tip`, semanticId, point(tipX, barMid), 'right');
    builder.hit(semanticId, box(layout.rowsX, rowTop, layout.rowsWidth, rowBottom - rowTop + 4));

    if (slot > 0) {
      const ahead = items[slot - 1]!;
      builder.relation(`${semanticId}.behind`, 'queued-behind', semanticId, queueItemId(ahead.id), `${item.label} waits behind ${ahead.label}`);
    }
  }

  const ribbonTop = layout.rowsTop - 8;
  const deadlineLabel = pickDeadlineLabel(spec, layout);
  builder.text(`${QUEUE_DEADLINE_ID}.label`, QUEUE_DEADLINE_ID, 'annotation', point(deadlineLabel.x, layout.plotTop + 12), deadlineLabel.text, {
    size: MICRO,
    mono: true,
    weight: 700,
    align: deadlineLabel.align,
    uppercase: true,
  });
  builder.line(`${QUEUE_DEADLINE_ID}.band.axis`, QUEUE_DEADLINE_ID, 'negative', point(layout.deadlineX, layout.bandY + layout.bandHeight), point(layout.deadlineX, layout.axisY));
  builder.anchor(`${QUEUE_DEADLINE_ID}.top`, QUEUE_DEADLINE_ID, point(layout.deadlineX, ribbonTop), 'up');
  builder.anchor(`${QUEUE_DEADLINE_ID}.axis`, QUEUE_DEADLINE_ID, point(layout.deadlineX, layout.axisY), 'down');
  builder.hit(QUEUE_DEADLINE_ID, box(layout.deadlineX - 12, ribbonTop, 24, layout.wallY - ribbonTop));

  // The doomed head of the queue is one group, not five unrelated peers.
  const doomedCount = layout.doomed.length;
  const braceTop = layout.rowsTop + layout.barOffset;
  const braceBottom = roundGeometry(layout.rowsTop + (doomedCount - 1) * layout.rowPitch + layout.barOffset + layout.barHeight);
  const spineX = roundGeometry(width - layout.margin - 20);
  builder.path(`${QUEUE_DOOMED_GROUP_ID}.brace`, QUEUE_DOOMED_GROUP_ID, 'negative', `M ${nn(spineX - 6)} ${nn(braceTop)} L ${nn(spineX)} ${nn(braceTop)} L ${nn(spineX)} ${nn(braceBottom)} L ${nn(spineX - 6)} ${nn(braceBottom)}`, {
    label: `${doomedCount} items are already past the client timeout`,
  });
  const braceLabel = pickBraceLabel(doomedCount, layout.wallY - layout.rowsTop);
  const braceLabelHalf = monoWidth(braceLabel, MICRO, true) / 2;
  const braceLabelY = clamp((braceTop + braceBottom) / 2, layout.rowsTop + braceLabelHalf, layout.wallY - braceLabelHalf);
  builder.text(`${QUEUE_DOOMED_GROUP_ID}.label`, QUEUE_DOOMED_GROUP_ID, 'annotation', point(width - layout.margin - 10, braceLabelY), braceLabel, {
    size: MICRO,
    mono: true,
    weight: 700,
    align: 'middle',
    uppercase: true,
    rotate: 90,
  });
  builder.anchor(`${QUEUE_DOOMED_GROUP_ID}.start`, QUEUE_DOOMED_GROUP_ID, point(spineX, braceTop), 'up');
  builder.anchor(`${QUEUE_DOOMED_GROUP_ID}.end`, QUEUE_DOOMED_GROUP_ID, point(spineX, braceBottom), 'down');
  builder.hit(QUEUE_DOOMED_GROUP_ID, box(spineX - 6, braceTop, 26, braceBottom - braceTop));

  // Capacity is a wall, not a number: nothing joins the queue below this line.
  builder.rect(`${QUEUE_CAPACITY_ID}.wall`, QUEUE_CAPACITY_ID, 'negative', box(layout.rowsX, layout.wallY, layout.rowsWidth, 3), {
    radius: 2,
    label: `capacity ${spec.capacity} slots`,
    interactive: true,
  });
  builder.anchor(`${QUEUE_CAPACITY_ID}.wall`, QUEUE_CAPACITY_ID, point(layout.rowsX + layout.rowsWidth / 2, layout.wallY), 'down');
  builder.hit(QUEUE_CAPACITY_ID, box(layout.rowsX, layout.wallY - 8, layout.rowsWidth, 21));

  if (layout.full) {
    builder.rect(`${QUEUE_REJECTED_ID}.band`, QUEUE_REJECTED_ID, 'capacity', box(layout.rowsX, layout.bandY, layout.rowsWidth, layout.bandHeight), {
      radius: 3,
      label: `${layout.shedPerSecond} arrivals per second are rejected at the wall`,
      interactive: true,
    });
    builder.text(`${QUEUE_REJECTED_ID}.label`, QUEUE_REJECTED_ID, 'annotation', point(layout.rowsX + 12, layout.bandY + layout.bandHeight / 2 + 4), rejectedText(spec, layout), {
      size: MICRO,
      mono: true,
      weight: 700,
      uppercase: true,
    });
    builder.anchor(`${QUEUE_REJECTED_ID}.center`, QUEUE_REJECTED_ID, point(layout.rowsX + layout.rowsWidth / 2, layout.bandY + layout.bandHeight / 2));
    builder.hit(QUEUE_REJECTED_ID, box(layout.rowsX, layout.bandY, layout.rowsWidth, layout.bandHeight));
    builder.relation(`${QUEUE_CAPACITY_ID}.rejects`, 'rejects', QUEUE_CAPACITY_ID, QUEUE_REJECTED_ID, `a full queue sheds ${layout.shedPerSecond} arrivals per second`);
  } else {
    const free = spec.capacity - items.length;
    const note = fitText([
      `CAPACITY ${spec.capacity} · ${free} SLOTS FREE · FILLING AT ${layout.shedPerSecond}/s`,
      `CAPACITY ${spec.capacity} · ${free} FREE · +${layout.shedPerSecond}/s`,
      `${free} OF ${spec.capacity} SLOTS FREE`,
    ], layout.rowsWidth - 4);
    builder.text(`${QUEUE_CAPACITY_ID}.note`, QUEUE_CAPACITY_ID, 'annotation', point(layout.rowsX, layout.bandY + layout.bandHeight / 2 + 4), note, {
      size: MICRO,
      mono: true,
      weight: 700,
      uppercase: true,
    });
  }

  // The queue's own depth, measured in time, directly under the bars it explains.
  if (layout.showDepthTime) {
    const bracketY = roundGeometry(layout.rowsBottom + 11);
    const armY = roundGeometry(bracketY - 7);
    builder.path(`${QUEUE_DEPTH_TIME_ID}.dimension`, QUEUE_DEPTH_TIME_ID, 'dimension', `M ${nn(layout.barX)} ${nn(armY)} L ${nn(layout.barX)} ${nn(bracketY)} L ${nn(layout.depthTimeX)} ${nn(bracketY)} L ${nn(layout.depthTimeX)} ${nn(armY)}`, {
      label: `${items.length} slots draining at ${spec.servicePerSecond} per second is ${layout.queueMs} ms of queue`,
    });
    const depthLabel = pickDepthLabel(spec, layout, items.length);
    builder.text(`${QUEUE_DEPTH_TIME_ID}.label`, QUEUE_DEPTH_TIME_ID, 'annotation', point(depthLabel.x, bracketY + 15), depthLabel.text, {
      size: MICRO,
      mono: true,
      weight: 700,
      align: depthLabel.align,
      uppercase: true,
    });
    builder.anchor(`${QUEUE_DEPTH_TIME_ID}.start`, QUEUE_DEPTH_TIME_ID, point(layout.barX, bracketY), 'left');
    builder.anchor(`${QUEUE_DEPTH_TIME_ID}.end`, QUEUE_DEPTH_TIME_ID, point(layout.depthTimeX, bracketY), 'right');
    builder.hit(QUEUE_DEPTH_TIME_ID, box(layout.barX, armY, layout.depthTimeX - layout.barX, 24));
    builder.relation(`${QUEUE_DEPTH_TIME_ID}.explains`, 'explains', QUEUE_DEPTH_TIME_ID, taughtId, `${items.length} slots at ${spec.servicePerSecond}/s is ${layout.queueMs} ms of queue before the head is served`);
  }

  // Age axis: the only quantity in the figure.
  builder.line(`${QUEUE_ID}.axis.line`, `${QUEUE_ID}.axis`, 'axis', point(layout.barX, layout.axisY), point(layout.barX + layout.barWidth, layout.axisY));
  for (let value = 0; value <= layout.domainMax + 0.5; value += layout.step) {
    const x = roundGeometry(layout.barX + (value / layout.domainMax) * layout.barWidth);
    const index = Math.round(value / layout.step);
    builder.line(`${QUEUE_ID}.axis.tick.${index}`, `${QUEUE_ID}.axis`, 'axis', point(x, layout.axisY), point(x, layout.axisY + 5));
    builder.text(`${QUEUE_ID}.axis.label.${index}`, `${QUEUE_ID}.axis`, 'annotation', point(x, layout.axisY + 18), String(value), {
      size: MICRO,
      mono: true,
      align: 'middle',
    });
  }
  if (spec.clientTimeoutMs % layout.step !== 0) {
    builder.line(`${QUEUE_DEADLINE_ID}.tick`, QUEUE_DEADLINE_ID, 'negative', point(layout.deadlineX, layout.axisY), point(layout.deadlineX, layout.axisY + 9));
  }
  builder.text(`${QUEUE_ID}.axis.title`, `${QUEUE_ID}.axis`, 'annotation', point(layout.barX + layout.barWidth, layout.axisY + 34), 'WAIT AGE (ms)', {
    size: MICRO,
    mono: true,
    weight: 700,
    align: 'end',
    uppercase: true,
  });
  builder.anchor(`${QUEUE_ID}.axis.origin`, `${QUEUE_ID}.axis`, point(layout.barX, layout.axisY), 'left');

  builder.relation(`${QUEUE_FLOW_ID}.drains`, 'serves', QUEUE_FLOW_ID, taughtId, `the head is served next, ${spec.servicePerSecond} per second`);
  builder.relation(`${QUEUE_DOOMED_GROUP_ID}.contains`, 'groups', QUEUE_DOOMED_GROUP_ID, taughtId, `${doomedCount} of ${items.length} queued items are already dead on arrival`);

  if (!layout.table) warnings.push('Queue uses the stacked density tier at this width.');
  if (layout.labelLineCount > 1) warnings.push('Row labels use the two-line wrap policy at this width.');
  if (layout.calloutCompact) warnings.push('Taught note uses the compact phrasing at this width.');
  if (layout.full) warnings.push(`Queue is saturated: ${layout.shedPerSecond} arrivals per second are shed at the wall.`);
  if (doomedCount === items.length) warnings.push('Every queued item is already past the client timeout.');
  const expectedHeadMs = (items.length / spec.servicePerSecond) * 1000;
  if (Math.abs(taught.waitedMs - expectedHeadMs) > expectedHeadMs * 0.6) {
    warnings.push(`Head age ${taught.waitedMs} ms does not follow from ${items.length} slots draining at ${spec.servicePerSecond}/s.`);
  }

  const active = spec.focus === 'doomed'
    ? [...layout.doomed.map((item) => queueItemId(item.id)), QUEUE_DEADLINE_ID, QUEUE_DOOMED_GROUP_ID]
    : spec.focus === 'capacity'
      ? [QUEUE_CAPACITY_ID, QUEUE_REJECTED_ID]
      : spec.focus === 'overview'
        ? [QUEUE_ID]
        : [taughtId];
  active.push(...(spec.emphasis ?? []));

  return finishFigure(spec, builder, {
    title: 'A bigger queue only ages the work',
    subtitle: `Arrivals outrun service by ${spec.arrivalsPerSecond - spec.servicePerSecond}/s, so the head of the queue passes the ${spec.clientTimeoutMs} ms client timeout before it is ever served. More slots buy more waiting, not more throughput.`,
    state: `${spec.focus ?? 'head'} · ${items.length}/${spec.capacity} queued · ${doomedCount} past ${spec.clientTimeoutMs}ms`,
    active,
    warnings: [...new Set(warnings)],
  });
}

export function validateQueueView(spec: QueueViewSpec): QueueLayout {
  validateViewport(spec.viewport.width, spec.viewport.height);
  if (!spec.provenance.trim()) throw new Error(`${FAMILY}: provenance is required`);
  if (!Number.isInteger(spec.capacity) || spec.capacity < 2 || spec.capacity > 8) throw new Error(`${FAMILY}: capacity must be an integer in 2…8`);
  if (spec.items.length < 2 || spec.items.length > spec.capacity) throw new Error(`${FAMILY}: require 2…capacity queued items`);
  if (!Number.isInteger(spec.arrivalsPerSecond) || spec.arrivalsPerSecond <= 0) throw new Error(`${FAMILY}: arrival rate must be a positive integer`);
  if (!Number.isInteger(spec.servicePerSecond) || spec.servicePerSecond <= 0) throw new Error(`${FAMILY}: service rate must be a positive integer`);
  if (spec.arrivalsPerSecond <= spec.servicePerSecond) throw new Error(`${FAMILY}: this view explains a queue under sustained overload; arrivals must exceed service`);
  if (!Number.isInteger(spec.clientTimeoutMs) || spec.clientTimeoutMs <= 0) throw new Error(`${FAMILY}: client timeout must be a positive integer`);

  const ids = spec.items.map((item) => item.id);
  if (new Set(ids).size !== ids.length) throw new Error(`${FAMILY}: item IDs must be unique`);
  spec.items.forEach((item, index) => {
    if (!/^[a-z][a-z0-9-]*$/.test(item.id)) throw new Error(`${FAMILY}: invalid item id ${item.id}`);
    if (!item.label.trim()) throw new Error(`${FAMILY}: item ${item.id} requires a label`);
    if (item.label.length > MAX_LABEL_CHARS) throw new Error(`${FAMILY}: item ${item.id} label exceeds ${MAX_LABEL_CHARS} characters`);
    if (!Number.isInteger(item.waitedMs) || item.waitedMs < 0) throw new Error(`${FAMILY}: item ${item.id} waitedMs must be a non-negative integer`);
    if (item.waitedMs > 600_000) throw new Error(`${FAMILY}: item ${item.id} exceeds the ten-minute teaching limit`);
    const ahead = spec.items[index - 1];
    if (ahead && ahead.waitedMs <= item.waitedMs) throw new Error(`${FAMILY}: items must run head first, strictly oldest to newest`);
  });
  if (!spec.items.some((item) => item.waitedMs > spec.clientTimeoutMs)) {
    throw new Error(`${FAMILY}: this view requires at least one item past the client timeout`);
  }

  return resolveLayout(spec);
}

function resolveLayout(spec: QueueViewSpec): QueueLayout {
  const { width, height } = spec.viewport;
  const narrow = width <= 430;
  const margin = width <= 340 ? 16 : narrow ? 18 : 24;
  const contentWidth = width - margin * 2;
  const railWidth = 24;
  const braceGutter = 26;
  const rowsX = roundGeometry(margin + railWidth);
  const rowsWidth = roundGeometry(contentWidth - railWidth - braceGutter);
  const indexWidth = width >= 480 ? 46 : 40;

  const chipTextWidth = Math.max(...spec.items.map((item) => monoWidth(`${item.waitedMs} ms`, VALUE)));
  const chipWidth = roundGeometry(chipTextWidth + 14);
  const chipHeight = 18;
  const maxLabelWidth = Math.max(...spec.items.map((item) => monoWidth(item.label, VALUE)));

  // Table tier only survives while the identity gutter leaves the age scale room to teach.
  const tableBarWidth = rowsWidth - indexWidth - (maxLabelWidth + 14) - (chipWidth + 10);
  const table = width >= 480 && tableBarWidth >= 230;

  const labelX = roundGeometry(rowsX + indexWidth);
  const labelBudget = table ? maxLabelWidth : rowsWidth - indexWidth - chipWidth - 10;
  const labelLines = spec.items.map((item) => (table ? [item.label] : wrapMono(item.label, labelBudget, VALUE, MAX_LABEL_LINES)));
  const labelLineCount = Math.max(...labelLines.map((lines) => lines.length));
  for (const lines of labelLines) {
    if (lines.some((line) => monoWidth(line, VALUE) > labelBudget + 0.5)) {
      throw new Error(`${FAMILY}: viewport is too narrow for the declared item labels`);
    }
  }

  const barX = table ? roundGeometry(labelX + maxLabelWidth + 14) : rowsX;
  const barWidth = roundGeometry(table ? rowsWidth - indexWidth - (maxLabelWidth + 14) - (chipWidth + 10) : rowsWidth);
  const chipX = roundGeometry(rowsX + rowsWidth - chipWidth);
  const barHeight = table ? 24 : 20;
  const rowGap = table ? 10 : 11;
  const barOffset = table ? 0 : labelLineCount * 15 + 3;
  const rowPitch = roundGeometry(barOffset + barHeight + rowGap);

  const doomed = spec.items.filter((item) => item.waitedMs > spec.clientTimeoutMs);
  const full = spec.items.length === spec.capacity;
  const shedPerSecond = spec.arrivalsPerSecond - spec.servicePerSecond;
  const summaryLines = summary(spec, doomed.length, narrow, contentWidth);

  const calloutTop = roundGeometry(45 + (summaryLines.length - 1) * 15 + 12);
  const calloutBudget = contentWidth - 19;
  const callout = pickCallout(spec, doomed.length, calloutBudget);
  const calloutHeight = roundGeometry(callout.lines.length * 16 + 2);
  const calloutWidth = roundGeometry(Math.min(contentWidth, Math.max(...callout.lines.map((line) => sansWidth(line, PROSE))) + 17));

  const maxWaited = Math.max(...spec.items.map((item) => item.waitedMs));
  const { step, domainMax } = chooseScale(maxWaited, spec.clientTimeoutMs, barWidth);
  const deadlineX = roundGeometry(barX + (spec.clientTimeoutMs / domainMax) * barWidth);
  // Queue depth divided by the service rate is the wait the queue creates by itself.
  const queueMs = Math.round((spec.items.length / spec.servicePerSecond) * 1000);
  const showDepthTime = queueMs >= domainMax * 0.12 && queueMs <= domainMax;
  const depthTimeX = roundGeometry(barX + (Math.min(queueMs, domainMax) / domainMax) * barWidth);

  const plotTop = roundGeometry(calloutTop + calloutHeight + 12);
  const rowsTop = roundGeometry(plotTop + 22);
  const bandHeight = table ? 24 : 22;
  const rowsGap = showDepthTime ? 34 : 9;
  const belowRows = rowsGap + 16 + bandHeight + 15 + 44;
  const minimumHeight = roundGeometry(rowsTop + (spec.capacity - 1) * rowPitch + barOffset + barHeight + belowRows);
  if (minimumHeight > height) {
    throw new Error(`${FAMILY}: viewport height cannot fit ${spec.capacity} readable slots at this width`);
  }

  // Spare height widens the row rhythm to a comfortable ceiling; it never stretches type.
  const spare = Math.floor((height - minimumHeight) / spec.capacity);
  const relaxedGap = rowGap + Math.max(0, Math.min(table ? 9 : 6, spare));
  const relaxedPitch = roundGeometry(barOffset + barHeight + relaxedGap);
  const rowsBottom = roundGeometry(rowsTop + (spec.capacity - 1) * relaxedPitch + barOffset + barHeight);
  const wallY = roundGeometry(rowsBottom + rowsGap);
  const bandY = roundGeometry(wallY + 16);
  const axisY = roundGeometry(bandY + bandHeight + 15);


  return {
    table,
    margin,
    contentWidth,
    railX: roundGeometry(margin + 18),
    rowsX,
    rowsWidth,
    indexWidth,
    labelX,
    labelLines,
    labelLineCount,
    barX,
    barWidth,
    barHeight,
    chipX,
    chipWidth,
    chipHeight,
    rowPitch: relaxedPitch,
    rowGap: relaxedGap,
    barOffset,
    summaryLines,
    calloutTop,
    calloutWidth,
    calloutHeight,
    calloutLines: callout.lines,
    calloutCompact: callout.compact,
    plotTop,
    rowsTop,
    rowsBottom,
    wallY,
    bandY,
    bandHeight,
    axisY,
    step,
    domainMax,
    deadlineX,
    queueMs,
    rowsGap,
    depthTimeX,
    showDepthTime,
    doomed,
    shedPerSecond,
    full,
  };
}

/**
 * Chooses a tick step that keeps every axis label an exact integer, leaves the
 * labels physically clear of each other, and — where the budget allows — lands
 * the client timeout on a labelled tick.
 */
function chooseScale(maxWaited: number, timeoutMs: number, barWidth: number): { step: number; domainMax: number } {
  const headroom = maxWaited * 1.04;
  const fits = (step: number): boolean => {
    const domainMax = Math.ceil(headroom / step) * step;
    const ticks = Math.round(domainMax / step);
    const labelWidth = monoWidth(String(domainMax), MICRO) + 14;
    return ticks >= 2 && ticks <= 8 && barWidth / ticks >= labelWidth;
  };
  const candidates = TICK_STEPS.filter(fits);
  const aligned = candidates.filter((step) => timeoutMs % step === 0);
  const step = aligned[0] ?? candidates[0] ?? TICK_STEPS.at(-1)!;
  return { step, domainMax: Math.ceil(headroom / step) * step };
}

function summary(spec: QueueViewSpec, doomedCount: number, narrow: boolean, room: number): string[] {
  const full = spec.items.length === spec.capacity;
  const queued = `${spec.items.length} OF ${spec.capacity} SLOTS ${full ? 'FULL' : 'USED'}`;
  const past = `${doomedCount} PAST DEADLINE`;
  const excess = spec.arrivalsPerSecond - spec.servicePerSecond;
  const rates = `IN ${spec.arrivalsPerSecond}/s · OUT ${spec.servicePerSecond}/s · ${full ? `SHED ${excess}/s` : `BACKLOG +${excess}/s`}`;
  const shortRates = `IN ${spec.arrivalsPerSecond}/s · OUT ${spec.servicePerSecond}/s`;
  if (!narrow && monoWidth(`${queued} · ${rates} · ${past}`, MICRO) <= room) return [`${queued} · ${rates} · ${past}`];
  return [
    fitText([`${queued} · ${past}`, `${spec.items.length}/${spec.capacity} · ${past}`], room, MICRO),
    fitText([rates, shortRates], room, MICRO),
  ];
}

function pickCallout(spec: QueueViewSpec, doomedCount: number, budget: number): { lines: string[]; compact: boolean } {
  const head = spec.items[0]!;
  const overdue = head.waitedMs - spec.clientTimeoutMs;
  const depth = spec.items.length === spec.capacity ? spec.capacity : spec.items.length;
  const queueMs = Math.round((depth / spec.servicePerSecond) * 1000);
  const variants = [
    `The head has waited ${head.waitedMs} ms — ${overdue} ms past the ${spec.clientTimeoutMs} ms client timeout. ${depth} slots draining at ${spec.servicePerSecond}/s is ${queueMs} ms of queue before anything is served, so ${doomedCount} ${doomedCount === 1 ? 'answer is' : 'answers are'} already unwanted.`,
    `Head waited ${head.waitedMs} ms, ${overdue} ms past the ${spec.clientTimeoutMs} ms timeout: ${depth} slots at ${spec.servicePerSecond}/s is ${queueMs} ms of queue before service.`,
    `${head.waitedMs} ms waited · ${overdue} ms past the timeout. ${depth} slots at ${spec.servicePerSecond}/s = ${queueMs} ms of queue.`,
  ];
  for (let index = 0; index < variants.length; index += 1) {
    const lines = wrapWords(variants[index]!, budget, PROSE, 2);
    if (lines) return { lines, compact: index > 0 };
  }
  return { lines: wrapWords(variants.at(-1)!, budget, PROSE, 3) ?? [variants.at(-1)!], compact: true };
}

function pickDeadlineLabel(spec: QueueViewSpec, layout: QueueLayout): { text: string; x: number; align: 'start' | 'end' } {
  const variants = [
    `CLIENT TIMEOUT ${spec.clientTimeoutMs} ms`,
    `TIMEOUT ${spec.clientTimeoutMs} ms`,
    `${spec.clientTimeoutMs} ms`,
  ];
  const rightRoom = spec.viewport.width - layout.margin - layout.deadlineX - 8;
  // Leftward placement runs back across the taught note's leader, so the label
  // may only claim the room between that leader and the deadline itself.
  const leftRoom = layout.deadlineX - 6 - (roundGeometry(layout.labelX - 10) + 6);
  for (const text of variants) {
    const textWidth = monoWidth(text, MICRO, true);
    if (textWidth <= rightRoom) return { text, x: roundGeometry(layout.deadlineX + 6), align: 'start' };
    if (textWidth <= leftRoom) return { text, x: roundGeometry(layout.deadlineX - 6), align: 'end' };
  }
  const shortest = variants.at(-1)!;
  return rightRoom >= leftRoom
    ? { text: shortest, x: roundGeometry(layout.deadlineX + 6), align: 'start' }
    : { text: shortest, x: roundGeometry(layout.deadlineX - 6), align: 'end' };
}

function pickDepthLabel(spec: QueueViewSpec, layout: QueueLayout, depth: number): { text: string; x: number; align: 'middle' | 'start' } {
  const span = layout.depthTimeX - layout.barX;
  const variants = [
    `${depth} SLOTS AT ${spec.servicePerSecond}/s = ${layout.queueMs} ms OF QUEUE`,
    `${depth} SLOTS AT ${spec.servicePerSecond}/s = ${layout.queueMs} ms`,
    `${layout.queueMs} ms OF QUEUE`,
  ];
  for (const text of variants) {
    if (monoWidth(text, MICRO, true) <= span - 10) {
      return { text, x: roundGeometry((layout.barX + layout.depthTimeX) / 2), align: 'middle' };
    }
  }
  return { text: variants.at(-1)!, x: roundGeometry(layout.depthTimeX + 6), align: 'start' };
}

function pickBraceLabel(doomedCount: number, span: number): string {
  const variants = [`${doomedCount} DEAD ON ARRIVAL`, `${doomedCount} DOOMED`, `×${doomedCount}`];

  for (const text of variants) {
    if (monoWidth(text, MICRO, true) <= span - 6) return text;
  }
  return variants.at(-1)!;
}

function rejectedText(spec: QueueViewSpec, layout: QueueLayout): string {
  return fitText([
    `CAPACITY ${spec.capacity} REACHED · ${layout.shedPerSecond}/s REJECTED AT THE DOOR`,
    `CAP ${spec.capacity} FULL · ${layout.shedPerSecond}/s REJECTED`,
    `${layout.shedPerSecond}/s REJECTED`,
  ], layout.rowsWidth - 24);
}

/** Picks the fullest phrasing that fits the room it has; never truncates. */
function fitText(variants: string[], room: number, size = MICRO): string {
  for (const text of variants) {
    if (monoWidth(text, size, true) <= room) return text;
  }
  return variants.at(-1)!;
}

function monoWidth(text: string, size: number, uppercase = false): number {
  return text.length * (size * MONO_ADVANCE + (uppercase ? UPPERCASE_TRACKING : 0));
}

function sansWidth(text: string, size: number): number {
  return text.length * size * SANS_ADVANCE;
}

function wrapWords(text: string, maxWidth: number, size: number, maxLines: number): string[] | undefined {
  const lines: string[] = [];
  let current = '';
  for (const word of text.split(' ')) {
    const candidate = current ? `${current} ${word}` : word;
    if (!current || sansWidth(candidate, size) <= maxWidth) {
      current = candidate;
      continue;
    }
    lines.push(current);
    current = word;
    if (lines.length >= maxLines) return undefined;
  }
  if (current) lines.push(current);
  if (lines.length > maxLines) return undefined;
  return lines.every((line) => sansWidth(line, size) <= maxWidth) ? lines : undefined;
}

/**
 * Wraps a machine label at its own separators, falling back to an exact
 * character break when a separator would leave the remainder unfittable.
 * Nothing is ever dropped: the validator rejects labels that cannot fit the
 * declared line budget instead of shortening them.
 */
function wrapMono(text: string, maxWidth: number, size: number, maxLines: number): string[] {
  const maxChars = Math.max(6, Math.floor(maxWidth / (size * MONO_ADVANCE)));
  if (text.length <= maxChars) return [text];
  const separated = sliceLines(text, maxChars, maxLines, true);
  if (separated.every((line) => line.length <= maxChars)) return separated;
  return sliceLines(text, maxChars, maxLines, false);
}

function sliceLines(text: string, maxChars: number, maxLines: number, useSeparators: boolean): string[] {
  const lines: string[] = [];
  let rest = text;
  while (rest.length > maxChars && lines.length < maxLines - 1) {
    const breakAt = useSeparators ? breakPoint(rest, maxChars) : maxChars;
    lines.push(rest.slice(0, breakAt));
    rest = rest.slice(breakAt);
  }
  lines.push(rest);
  return lines;
}

function breakPoint(text: string, maxChars: number): number {
  for (const separators of ['/ ', '-_?&.:']) {
    for (let index = maxChars; index > 4; index -= 1) {
      if (separators.includes(text[index - 1]!)) return index;
    }
  }
  return maxChars;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function nn(value: number): number {
  return Math.round(value * 10) / 10;
}
